import React, { useEffect } from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Avatar } from "../../design/avatar";
import TeamMemberRoleBadge from "../../components/TeamMemberRoleBadge";
import DeleteTeamMemberButton from "./components/DeleteTeamMemberButton";
import { useAuth } from "../../contexts/AuthContext";
import EmptyStateView from "../../components/EmptyStateView";
import { useTeamMember } from "../../contexts/TeamMemberContext";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import CreateInvitationButton from "../../components/CreateInvitationButton";
import { useInvitation } from "../../contexts/InvitationContext";
import { useMatter } from "../../contexts/MatterContext";

const TeamMemberListPage: React.FC = () => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const { currentTeamMember, teamMembers, invitations, loading, error } =
    useTeamMember();

  if (loading.list) {
    return <LoadingView title="Loading Team Members" />;
  }
  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const filteredInvitations = invitations.filter(
    (invitation) =>
      invitation.status === "pending" && invitation.matter === currentMatterId,
  );

  return (
    <>
      <div className="flex items-end justify-between gap-4">
        <Heading>Team</Heading>
        <div className="flex gap-4">
          <CreateInvitationButton />
        </div>
      </div>
      {teamMembers.length === 0 && (
        <EmptyStateView
          title="No Team Members found"
          message="Create a Team Member to get started."
        />
      )}
      {teamMembers.length > 0 && (
        <Table className="mt-8 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader />
            </TableRow>
          </TableHead>
          <TableBody>
            {teamMembers.map((teamMember) => (
              <TableRow
                key={teamMember._id}
                href={`/team-member/${teamMember._id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {teamMember.user?.avatarUrl && (
                      <Avatar
                        src={teamMember.user?.avatarUrl}
                        className="size-6"
                        alt={`${teamMember.user?.name}`}
                        initials={teamMember.user?.initials}
                      />
                    )}
                    <span className="font-bold">{`${teamMember.user?.name}`}</span>
                  </div>
                </TableCell>
                <TableCell>{teamMember.user?.email}</TableCell>
                <TableCell>
                  <TeamMemberRoleBadge role={teamMember.role} />
                </TableCell>
                <TableCell>
                  {currentTeamMember?.role === "admin" &&
                    teamMember._id !== user?.uid && (
                      <DeleteTeamMemberButton teamMemberId={teamMember._id} />
                    )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {filteredInvitations.length > 0 && (
        <div className="mt-8">
          <Heading>Invitations</Heading>
          <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
            <TableHead>
              <TableRow>
                <TableHeader>Email</TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvitations.map((invitation) => (
                <TableRow key={invitation._id}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    {/* Add any action buttons for the invitation here */}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
};

export default TeamMemberListPage;
